import { ParamMap } from '@angular/router';
import { List } from 'immutable';
import { Dashboard } from '../reports/models/dashboard.model';
import { ReportType } from '../reports/models/report-type.model';
import { Report } from '../reports/models/report.model';
import { HomeMetrics } from './models/home-metrics.model';
import { ReportExpandedViewTypesEnum } from './report-data.data';
import { ExpandedSummary } from './models/expanded-summary.model';

// Actions
export enum ActionsEnum {
  RESET_REPORTS_DATA = 'RESET_REPORTS_DATA',
  FETCH_DASHBOARD = 'FETCH_DASHBOARD',
  FETCH_DASHBOARD_SUCCESS = 'FETCH_DASHBOARD_SUCCESS',
  FETCH_DASHBOARD_ERROR = 'FETCH_DASHBOARD_ERROR',
  UPDATE_DASHBOARD = 'UPDATE_DASHBOARD',
  FETCH_REPORT = 'FETCH_REPORT',
  FETCH_REPORT_SUCCESS = 'FETCH_REPORT_SUCCESS',
  FETCH_REPORT_ERROR = 'FETCH_REPORT_ERROR',
  UPDATE_CURRENT_REPORT = 'UPDATE_CURRENT_REPORT',
  CLEAR_CURRENT_REPORT = 'CLEAR_CURRENT_REPORT',
  UPDATE_REPORT_TYPES = 'UPDATE_REPORT_TYPES',
  UPDATE_CURRENT_REPORT_FILTER_VALUES = 'UPDATE_CURRENT_REPORT_FILTER_VALUES',
  FETCH_HOME_METRICS = 'FETCH_HOME_METRICS',
  FETCH_HOME_METRICS_SUCCESS = 'FETCH_HOME_METRICS_SUCCESS',
  FETCH_HOME_METRICS_ERROR = 'FETCH_HOME_METRICS_ERROR',
  UPDATE_HOME_METRICS = 'UPDATE_HOME_METRICS',
  REGISTER_HOME_METRICS_POLLING = 'REGISTER_HOME_METRICS_POLLING',
  UNREGISTER_HOME_METRICS_POLLING = 'UNREGISTER_HOME_METRICS_POLLING',
  // Insights hub
  FETCH_EXPANDED_VIEW = 'FETCH_EXPANDED_VIEW',
  FETCH_EXPANDED_VIEW_SUCCESS = 'FETCH_EXPANDED_VIEW_SUCCESS',
}

// Action Payloads

export interface IFetchDashboardPayload {
  params: { [paramKey: string]: string };
}

export interface IFetchReportPayload {
  reportType: ReportType;
  queryParams: ParamMap;
}

export interface IReportDataPayload {
  report: Report;
  reportType: ReportType;
}

export interface IUpdateHomeMetricsPayload {
  homeMetrics: HomeMetrics;
}

export interface IRegisterHomeMetricsPolling {
  interval: number;
}

export interface IFetchExpandedViewPayload {
  types: ReportExpandedViewTypesEnum[];
  currencyCode: string;
  topItemsLimit?: number;
}

export interface IFetchExpandedViewSuccessPayload {
  expandedSummaryItems: List<ExpandedSummary>;
}

// Action interfaces

export interface ResetReportsData {
  type: ActionsEnum.RESET_REPORTS_DATA;
}

export interface FetchDasboard {
  type: ActionsEnum.FETCH_DASHBOARD;
  payload: IFetchDashboardPayload;
}

export interface FetchDashboardSuccess {
  type: ActionsEnum.FETCH_DASHBOARD_SUCCESS;
}

export interface FetchDashboardError {
  type: ActionsEnum.FETCH_DASHBOARD_ERROR;
  payload: any[];
}

export interface UpdateDashboard {
  type: ActionsEnum.UPDATE_DASHBOARD;
  payload: any;
}

export interface FetchReport {
  type: ActionsEnum.FETCH_REPORT;
  payload: IFetchReportPayload;
}

export interface FetchReportSuccess {
  type: ActionsEnum.FETCH_REPORT_SUCCESS;
  payload: IReportDataPayload;
}

export interface FetchReportError {
  type: ActionsEnum.FETCH_REPORT_ERROR;
  payload: any[];
}

export interface UpdateCurrentReport {
  type: ActionsEnum.UPDATE_CURRENT_REPORT;
  payload: IReportDataPayload;
}

export interface ClearCurrentReport {
  type: ActionsEnum.CLEAR_CURRENT_REPORT;
}

export interface UpdateReportTypes {
  type: ActionsEnum.UPDATE_REPORT_TYPES;
  payload: any;
}

export interface UpdateCurrentReportFilterValues {
  type: ActionsEnum.UPDATE_CURRENT_REPORT_FILTER_VALUES;
  payload: any;
}

export interface FetchHomeMetrics {
  type: ActionsEnum.FETCH_HOME_METRICS;
}

export interface FetchHomeMetricsSuccess {
  type: ActionsEnum.FETCH_HOME_METRICS_SUCCESS;
}

export interface FetchHomeMetricsError {
  type: ActionsEnum.FETCH_HOME_METRICS_ERROR;
  payload: any[];
}

export interface UpdateHomeMetrics {
  type: ActionsEnum.UPDATE_HOME_METRICS;
  payload: IUpdateHomeMetricsPayload;
}

export interface RegisterHomeMetricsPolling {
  type: ActionsEnum.REGISTER_HOME_METRICS_POLLING;
  payload: IRegisterHomeMetricsPolling;
}

export interface UnregisterHomeMetricsPolling {
  type: ActionsEnum.UNREGISTER_HOME_METRICS_POLLING;
}

export interface FetchExpandedView {
  type: ActionsEnum.FETCH_EXPANDED_VIEW;
  payload: IFetchExpandedViewPayload;
}

export interface FetchExpandedViewSuccess {
  type: ActionsEnum.FETCH_EXPANDED_VIEW_SUCCESS;
  payload: IFetchExpandedViewSuccessPayload;
}

// Action definitions

export const resetReportsData = (): ResetReportsData => ({
  type: ActionsEnum.RESET_REPORTS_DATA,
});
export const fetchDashboard = (
  params?: IFetchDashboardPayload
): FetchDasboard => ({
  type: ActionsEnum.FETCH_DASHBOARD,
  payload: params,
});
export const fetchDashboardSuccess = (): FetchDashboardSuccess => ({
  type: ActionsEnum.FETCH_DASHBOARD_SUCCESS,
});
export const fetchDashboardError = (error: any[]): FetchDashboardError => ({
  type: ActionsEnum.FETCH_DASHBOARD_ERROR,
  payload: error,
});
export const updateDashboard = (dashboard: Dashboard): UpdateDashboard => ({
  type: ActionsEnum.UPDATE_DASHBOARD,
  payload: dashboard,
});
export const fetchReport = (
  reportType: ReportType,
  queryParams: ParamMap
): FetchReport => ({
  type: ActionsEnum.FETCH_REPORT,
  payload: {
    reportType,
    queryParams,
  },
});
export const fetchReportSuccess = (
  report: Report,
  reportType: ReportType
): FetchReportSuccess => ({
  type: ActionsEnum.FETCH_REPORT_SUCCESS,
  payload: {
    report,
    reportType,
  },
});
export const fetchReportError = (error: any[]): FetchReportError => ({
  type: ActionsEnum.FETCH_REPORT_ERROR,
  payload: error,
});
export const updateCurrentReport = (
  report: Report,
  reportType: ReportType
): UpdateCurrentReport => ({
  type: ActionsEnum.UPDATE_CURRENT_REPORT,
  payload: {
    report,
    reportType,
  },
});
export const clearCurrentReport = (): ClearCurrentReport => ({
  type: ActionsEnum.CLEAR_CURRENT_REPORT,
});
export const updateReportTypes = (
  reportTypes: List<ReportType>
): UpdateReportTypes => ({
  type: ActionsEnum.UPDATE_REPORT_TYPES,
  payload: reportTypes,
});
export const updateCurrentReportFilterValues = (
  filterValues: any
): UpdateCurrentReportFilterValues => ({
  type: ActionsEnum.UPDATE_CURRENT_REPORT_FILTER_VALUES,
  payload: filterValues,
});
export const fetchHomeMetrics = (): FetchHomeMetrics => ({
  type: ActionsEnum.FETCH_HOME_METRICS,
});
export const fetchHomeMetricsSuccess = (): FetchHomeMetricsSuccess => ({
  type: ActionsEnum.FETCH_HOME_METRICS_SUCCESS,
});
export const fetchHomeMetricsError = (error: any[]): FetchHomeMetricsError => ({
  type: ActionsEnum.FETCH_HOME_METRICS_ERROR,
  payload: error,
});
export const updateHomeMetrics = (
  homeMetrics: HomeMetrics
): UpdateHomeMetrics => ({
  type: ActionsEnum.UPDATE_HOME_METRICS,
  payload: {
    homeMetrics,
  },
});
export const registerHomeMetricsPolling = (
  interval: number
): RegisterHomeMetricsPolling => ({
  type: ActionsEnum.REGISTER_HOME_METRICS_POLLING,
  payload: {
    interval,
  },
});
export const unregisterHomeMetricsPolling = (): UnregisterHomeMetricsPolling => ({
  type: ActionsEnum.UNREGISTER_HOME_METRICS_POLLING,
});

export const fetchExpandedView = (
  types: ReportExpandedViewTypesEnum[],
  currencyCode: string,
  topItemsLimit?: number
): FetchExpandedView => ({
  type: ActionsEnum.FETCH_EXPANDED_VIEW,
  payload: {
    types,
    currencyCode,
    topItemsLimit,
  },
});

export const fetchExpandedViewSuccess = (
  expandedSummaryItems: List<ExpandedSummary>
): FetchExpandedViewSuccess => ({
  type: ActionsEnum.FETCH_EXPANDED_VIEW_SUCCESS,
  payload: {
    expandedSummaryItems,
  },
});
